import { Box, Grid, Paper, Typography, Chip } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import arrayMove from 'array-move';
import React, { useEffect, useRef, useState } from 'react';
import { SortableContainer as sortableContainer, SortableElement as sortableElement } from 'react-sortable-hoc';

import NoImageIcon from '../../../assets/Svg/NoImageIcon';
import FormTextField, { IFormTextFieldImperativeHandle } from '../../../components/FormTextField';
import { ImageUpload } from '../../../components/ImageUpload';
import ObjectSelector from '../../../components/ObjectSelector';
import { SubmitControl } from '../../../components/SubmitControl';
import { useLane, useLaneMutation } from '../../../hooks/queries/useOnboarding';
import useConfirmDialog from '../../../hooks/useConfirmDialog';
import { getCollection } from '../../../services/api/Collection';
import { uploadFile } from '../../../services/api/S3';
import { ICollection, IOnboardingCollectionMapper, IOnboardingLane } from '../../../services/types';
import CollectionMapper from './CollectionMapper';
import { LiveOnFlowsList } from './Popovers';

interface IProps {
  laneId?: number;
  onCreate?: (lane: IOnboardingLane) => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  infoWrap: {
    padding: '30px 25px',
    marginBottom: 30,
  },
  inputLabel: {
    fontSize: 15,
    fontWeight: 'normal',
    color: '#212121',
  },
  imageUploader: {
    marginTop: 8,
    width: 414,
  },
  imageDescription: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  dualTableWrap: {
    display: 'flex',
    minHeight: 300,
  },
  dualTableLeftWrap: {
    flex: 1,
  },
  dualTableRightWrap: {
    borderLeft: '1px solid #dcdcdc',
    flex: '0 0 320px',
  },
  dualTableRightBox: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  dualTableRightHeader: {
    backgroundColor: '#eee',
    padding: '15px 24px 15px',
    lineHeight: '1rem',
    borderBottom: '1px solid rgba(224, 224, 224, 1)',
  },
  dualTableRightHeaderText: {
    color: 'rgba(0,0,0, 0.54)',
    fontSize: 12,
    fontWeight: 500,
    whiteSpace: 'nowrap',
  },
  dualTableRightContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: '10px 0',
  },
  collectionLabel: {
    marginBottom: 13,
  },
  collectionsContainer: { overflow: 'hidden' },
  collectionListContainer: {},
  helper: {
    marginLeft: -5,
    borderRadius: '4px',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    boxShadow: '0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)',
  },
  infoListItem: {
    marginTop: 5,
  },
  link: {
    color: theme.palette.primary.main,
  },
  popoverItemTitle: {
    flexGrow: 1,
  },
  popoverPriority: {
    fontSize: 14,
    color: theme.palette.primary.main,
  },
}));

const createEmptyLane = (): Partial<IOnboardingLane> => ({ matureCollectionMappers: [], nonMatureCollectionMappers: [] });

const SortableContainerBox = sortableContainer((props: any) => {
  const { children } = props;
  const classes = useStyles();

  return <Box className={classes.collectionListContainer}>{children}</Box>;
});
const SortableMapperItem = sortableElement(CollectionMapper);

const LaneView = (props: IProps): JSX.Element => {
  const classes = useStyles();
  const { laneId, onCreate } = props;

  const confirm = useConfirmDialog();

  // useQueries
  const { data: dataFromQuery, isLoading } = useLane(laneId);
  const { create, update } = useLaneMutation();

  // useRefs
  const titleRef = useRef<IFormTextFieldImperativeHandle>();
  const themeRef = useRef<IFormTextFieldImperativeHandle>();
  const segmentRef = useRef<IFormTextFieldImperativeHandle>();

  // useStates
  const [lane, setLane] = useState<IOnboardingLane | Partial<IOnboardingLane>>(createEmptyLane());
  const [isPendingImageUpload, setIsPendingImageUpload] = useState<boolean>(false);
  const [themeNameRefs, setThemeNameRefs] = useState<React.MutableRefObject<IFormTextFieldImperativeHandle | undefined>[]>([]);

  // form states
  const [mode, setMode] = useState<'create' | 'edit' | 'view'>(laneId ? 'view' : 'create');
  const isEditing = mode !== 'view';
  const getValidationMessages = () => {
    const messages = [
      ...(titleRef.current?.getValidationMessages() || []),
      ...(themeRef.current?.getValidationMessages() || []),
      ...(segmentRef.current?.getValidationMessages() || []),
      ...(themeNameRefs.map(ref => ref.current?.getValidationMessages() || []).flatMap(msg => msg) || []),
    ];
    if (!lane.thumbUrl) {
      messages.push('Theme Image required.');
    }
    if (isPendingImageUpload) {
      messages.push('Theme Image uploading.');
    }

    return messages;
  };

  // handlers
  const handleEditClick = () => {
    setMode('edit');
  };

  const handleCancelClick = () => {
    const hasChanges = JSON.stringify(dataFromQuery) !== JSON.stringify(lane);

    if (hasChanges) {
      confirm.open({
        title: 'Wait!',
        text: 'You have unsaved changes. Are you sure you want to discard?',
        onProceed: () => {
          reset();
        },
      });
    } else {
      reset();
    }
  };

  const reset = () => {
    if (mode === 'edit' && dataFromQuery) {
      setLane(dataFromQuery);
      setMode('view');
    } else {
      setLane(createEmptyLane());
    }
  };

  const handleSaveClick = async () => {
    // remove unnecessary data. api post data size limit.
    const purifiedLane = {
      ...lane,
      matureCollectionMappers: lane.matureCollectionMappers?.map(mapper => ({
        trackingId: mapper.trackingId,
        themeName: mapper.themeName,
        priority: mapper.priority,
        segmentId: mapper.segmentId || null,
      })),
      nonMatureCollectionMappers: lane.nonMatureCollectionMappers?.map(mapper => ({
        trackingId: mapper.trackingId,
        themeName: mapper.themeName,
        priority: mapper.priority,
        segmentId: mapper.segmentId || null,
      })),
    };
    const result = await (laneId ? update : create).mutateAsync(purifiedLane);
    if (mode === 'create') {
      setLane({});
    }
    if (mode === 'edit') {
      setMode('view');
    }
    if (!laneId && onCreate) {
      onCreate(result);
    }
  };

  const handleFileLoaded = async (file: File, blob: Blob, encodedImage: string, extName: string) => {
    setLane(ps => ({ ...ps, thumbUrl: encodedImage }));

    try {
      setIsPendingImageUpload(true);
      const result = await uploadFile(`image.${extName}`, blob, 'onboarding-lane');

      if (result?.data && result?.status === 200) {
        setLane(ps => ({ ...ps, thumbUrl: result.data.url }));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsPendingImageUpload(false);
    }
  };

  const handleCollectionAdd = async (isMature: boolean, collection: ICollection) => {
    const [fetchPromise] = getCollection(collection.trackingId!);
    const fetchedCollection = await fetchPromise;
    const targetMappers = (isMature ? lane.matureCollectionMappers : lane.nonMatureCollectionMappers) || [];
    const newMapper: Partial<IOnboardingCollectionMapper> = {
      collection: fetchedCollection.data,
      isMature,
      trackingId: collection.trackingId,
      priority: targetMappers?.length ? targetMappers.length + 1 : 1,
      contentTypes: collection.storyTypes,
    };
    if (isMature) {
      setLane(ps => ({ ...ps, matureCollectionMappers: [...targetMappers, newMapper] }));
    } else {
      setLane(ps => ({ ...ps, nonMatureCollectionMappers: [...targetMappers, newMapper] }));
    }
  };

  const handleMatureSort = ({ newIndex, oldIndex }: { newIndex: number; oldIndex: number }) => {
    setLane(ps => ({ ...ps, matureCollectionMappers: sortArray(ps.matureCollectionMappers!, { newIndex, oldIndex }) }));
  };

  const handleNonMatureSort = ({ newIndex, oldIndex }: { newIndex: number; oldIndex: number }) => {
    setLane(ps => ({ ...ps, nonMatureCollectionMappers: sortArray(ps.nonMatureCollectionMappers!, { newIndex, oldIndex }) }));
  };

  const sortArray = (srcArray: Array<Partial<IOnboardingCollectionMapper>>, { newIndex, oldIndex }: { newIndex: number; oldIndex: number }) => {
    const sorted = arrayMove([...srcArray], oldIndex, newIndex);
    sorted.forEach((t, i) => (t.priority = i + 1));
    return sorted;
  };

  // useEffects
  useEffect(() => {
    // replace initial state to loaded data
    if (dataFromQuery) {
      setLane(dataFromQuery);
    }
  }, [dataFromQuery]);

  return (
    <Box width={'1080px'}>
      <Box display="flex" alignItems="center" paddingBottom={2}>
        <Typography variant="h6">{mode === 'create' ? 'Create Lane' : lane?.title || ''}</Typography>
        <SubmitControl
          mode={mode}
          onEditClick={handleEditClick}
          validationMessages={getValidationMessages()}
          onSubmitClick={handleSaveClick}
          onCancelClick={handleCancelClick}
        />
      </Box>
      <Paper className={classes.infoWrap}>
        <Grid container>
          <Grid item xs={6} style={{ paddingRight: '145px' }}>
            <Box marginBottom={'30px'}>
              <Typography className={''}>Lane Title (internal)</Typography>
              <FormTextField
                defaultValue={lane.title}
                name="Title"
                placeholder="Add title"
                isEditing={isEditing}
                isLoading={isLoading}
                required={true}
                fullWidth
                ref={titleRef}
                onValueSubmit={(title: string) => {
                  setLane(ps => ({ ...ps, title }));
                }}
                className={''}
              />
            </Box>
            <Box marginBottom={'30px'}>
              <Typography className={''}>Theme Name</Typography>
              <FormTextField
                defaultValue={lane.themeName}
                name="Theme Name"
                placeholder="Add theme name"
                isEditing={isEditing}
                isLoading={isLoading}
                required={true}
                fullWidth
                ref={themeRef}
                onValueSubmit={(themeName: string) => {
                  setLane(ps => ({ ...ps, themeName }));
                }}
                className={''}
                maxLength={35}
                showTextCount={true}
              />
            </Box>
            <Box marginBottom={'30px'}>
              <Typography className={''}>Segment ID</Typography>
              <FormTextField
                inputProps={{ type: 'number' }}
                defaultValue={String(lane.segmentId || '')}
                name="Segment ID"
                placeholder="Add segment ID"
                isEditing={isEditing}
                isLoading={isLoading}
                required={false}
                fullWidth
                ref={segmentRef}
                onValueSubmit={(segmentId: string) => {
                  setLane(ps => ({ ...ps, segmentId: Number(segmentId) || null }));
                }}
                className={''}
              />
            </Box>
            {!isEditing && lane.flowLaneMappers?.length && (
              <>
                <Box>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Typography>Live on</Typography>
                    </Grid>
                    <LiveOnFlowsList mappers={lane.flowLaneMappers} />
                  </Grid>
                </Box>
              </>
            )}
          </Grid>
          <Grid item xs={6}>
            <Typography className={''}>Theme Image</Typography>
            <ImageUpload
              onFileLoaded={handleFileLoaded}
              src={lane.thumbUrl}
              readonly={mode === 'view'}
              isPending={isPendingImageUpload}
              placeholderRenderer={() => <NoImageIcon />}
              className={classes.imageUploader}
              imageWrapClassName={''}
              descriptionClassName={classes.imageDescription}
            />
          </Grid>
        </Grid>
      </Paper>
      <Box marginBottom={'30px'}>
        <Typography variant="h6" className={classes.collectionLabel}>
          Collections
        </Typography>
        <Paper className={classes.collectionsContainer}>
          <Box className={isEditing ? classes.dualTableWrap : ''}>
            <Box className={classes.dualTableLeftWrap}>
              <SortableContainerBox lockAxis="y" useDragHandle={true} onSortEnd={handleMatureSort} helperClass={classes.helper}>
                {lane?.matureCollectionMappers?.map((collectionMapper, i) => (
                  <SortableMapperItem
                    index={i}
                    disabled={!isEditing}
                    key={`mature-collection-item-${collectionMapper.collection!.id}`}
                    collectionMapper={collectionMapper}
                    isEditing={isEditing}
                    onMount={ref => {
                      ref && setThemeNameRefs(ps => [...ps, ref]);
                    }}
                    onChange={(mapper: Partial<IOnboardingCollectionMapper>) => {
                      const newMappers = [...lane.matureCollectionMappers!];
                      newMappers[i] = mapper;
                      setLane(ps => ({ ...ps, matureCollectionMappers: newMappers }));
                    }}
                    onDelete={() => {
                      const newMappers = [...lane.matureCollectionMappers!];
                      newMappers.splice(i, 1).map((item, idx) => ({ ...item, priority: idx + 1 }));
                      setLane(ps => ({ ...ps, matureCollectionMappers: newMappers }));
                    }}
                  />
                ))}
              </SortableContainerBox>
            </Box>
            {isEditing && (
              <Box className={classes.dualTableRightWrap}>
                <Box className={classes.dualTableRightBox}>
                  <ObjectSelector
                    type="collection"
                    isOnboarding={true}
                    className={classes.dualTableRightContent}
                    selectedIds={lane.matureCollectionMappers?.map(mapper => mapper.collection!.id!) || []}
                    selectLimit={50}
                    onAddCollection={collection => handleCollectionAdd(true, collection)}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
      <Box>
        <Typography variant="h6" className={classes.collectionLabel}>
          Collections for non-mature readers
        </Typography>
        <Paper className={classes.collectionsContainer}>
          <Box className={isEditing ? classes.dualTableWrap : ''}>
            <Box className={classes.dualTableLeftWrap}>
              <SortableContainerBox lockAxis="y" useDragHandle={true} onSortEnd={handleNonMatureSort} helperClass={classes.helper}>
                {lane?.nonMatureCollectionMappers?.map((collectionMapper, i) => (
                  <SortableMapperItem
                    index={i}
                    disabled={!isEditing}
                    key={`mature-collection-item-${collectionMapper.collection?.id}`}
                    collectionMapper={collectionMapper}
                    isEditing={isEditing}
                    onMount={ref => {
                      ref && setThemeNameRefs(ps => [...ps, ref]);
                    }}
                    onChange={(mapper: Partial<IOnboardingCollectionMapper>) => {
                      const newMappers = [...lane.nonMatureCollectionMappers!];
                      newMappers[i] = mapper;
                      setLane(ps => ({ ...ps, nonMatureCollectionMappers: newMappers }));
                    }}
                    onDelete={() => {
                      const newMappers = [...lane.nonMatureCollectionMappers!];
                      newMappers.splice(i, 1).map((item, idx) => ({ ...item, priority: idx + 1 }));
                      setLane(ps => ({ ...ps, nonMatureCollectionMappers: newMappers }));
                    }}
                  />
                ))}
              </SortableContainerBox>
            </Box>
            {isEditing && (
              <Box className={classes.dualTableRightWrap}>
                <Box className={classes.dualTableRightBox}>
                  <ObjectSelector
                    type="collection"
                    isOnboarding={true}
                    className={classes.dualTableRightContent}
                    selectedIds={lane.nonMatureCollectionMappers?.map(mapper => mapper.collection!.id!) || []}
                    selectLimit={50}
                    onAddCollection={collection => handleCollectionAdd(false, collection)}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default LaneView;
